<script>
    import { LayerCake, Html } from "layercake";

    import { feature } from "topojson-client";
    import { geoAlbersUsa } from "d3-geo";
    import { onMount } from "svelte";
    import {
        titleCase,
        colorScale,
        the,
        formatRemainsOf,
    } from "../scripts/helpers.js";
    import { fade } from "svelte/transition";

    import Map from "../_components/Map.svelte";
    import Tooltip from "../_components/Tooltip.svelte";

    // @ts-expect-error (dataset: import)
    import guff from "dataset:../_data/guff.aml";
    // @ts-expect-error
    import usStates from "dataset:../_data/geo/states-10m.json";
    // @ts-expect-error
    import counties from "dataset:../_data/geo/counties-10m.json";
    // @ts-expect-error
    import countries from "dataset:../_data/geo/countries-10m.json";

    // @ts-expect-error
    import highest_mni_in_a_county from "dataset:../_data/summary/highest_mni_in_a_county.csv";

    /** @type { import("../_data/institutions/getSubunits.js").Subunit[] } */
    let subunits = [];
    /**
     * See county_pct_complete.csv
     * @type {
            {
                fips: string,
                county: string,
                state_abbreviation: string,
                state: string,
                pending: string,
                complete: string,
                total: string,
                pct_complete: string
            }[]
        }
     */
    let countiesData = [];
    /** @type {
        import("../_data/tribes/byTribe.js").StateDataOnTribeDetail[] |
        import("../_data/institutions/byInstitution.js").StateDataOnInstitutionDetail[]
    } */
    let stateData = [];

    const stateGeojson = feature(usStates, usStates.objects.states);
    const countiesGeojson = feature(counties, counties.objects.counties);
    const countriesGeojson = feature(countries, countries.objects.countries);
    const nationGeojson = feature(countries, countries.objects.nation);
    /**
     * @type { import("geojson").Feature | null }
     * Data actually gets loaded in `onMount` for homepage (stays null for
     * other pages)
    */
    let riversGeojson = null;
    const projection = geoAlbersUsa;

    /** @type { CustomEvent | {} }} */
    let event = {};
    // This will be the initial value used for the <Map> as well, regardless of
    // what that component says
    let hideTooltip = true;

    let ready = false;
    let name = "";

    export let switchToHtmlMap;
    export let ctx;
    export let swipeSvg;
    export let window_width;

    onMount(async () => {
        if (ctx["type"] == "homepage") {
            const homepageOnlyDataPromises = [
                import(
                    // @ts-expect-error
                    "dataset:../_data/geo/county_pct_complete.csv"
                ),
                import(
                    // @ts-expect-error
                    "dataset:../_data/geo/rivers.json"
                ),
            ];
            const homepageOnlyData = await Promise.all(
                homepageOnlyDataPromises
            );
            countiesData = homepageOnlyData[0].default;
            const rivers = homepageOnlyData[1].default;
            riversGeojson = feature(rivers, rivers.objects.rivers);
        } else if (ctx["type"] == "institution" || ctx["type"] == "tribe") {
            countiesData = JSON.parse(
                // @ts-ignore we'll assume all institution and tribe pages have
                // this element in valid form
                document.getElementById("countyData").textContent
            );

            stateData = JSON.parse(
                // @ts-ignore we'll assume all institution and tribe pages have
                // this element in valid form
                document.getElementById("stateData").textContent
            );

            subunits = JSON.parse(
                // @ts-ignore we'll assume all institution and tribe pages have
                // this element in valid form
                document.getElementById("subunitData").textContent
            );

            // @ts-ignore we'll assume all institution and tribe pages have
            // this element in valid form
            name = document.getElementById("name").textContent;
        }
        ready = true;
    });
</script>

<div class=" full-size-reset-wrapper">
    <div class="full-size-reset full" style="margin-bottom: 0; margin-top: 0;">
        <div class="mobile-map-wrapper">
            {#if switchToHtmlMap && ctx["type"] != "homepage"}
                <img
                    class="swipe-svg"
                    src={swipeSvg}
                    alt="Swipe interaction icon"
                />
            {/if}
            <div
                class="chart-container center"
                transition:fade
                on:mouseleave={() => {
                    hideTooltip = true;
                }}
            >
                {#if ready}
                    <LayerCake
                        data={{
                            stateGeojson: stateGeojson,
                            countiesGeojson: countiesGeojson,
                            countriesGeojson: countriesGeojson,
                            nationGeojson: nationGeojson,
                            riversGeojson: riversGeojson,
                            subunits: subunits,
                            countiesData: countiesData,
                            stateData: stateData,
                        }}
                    >
                        <Map
                            {guff}
                            {colorScale}
                            spikeMaxVal={highest_mni_in_a_county[0].number_mni}
                            {projection}
                            {ctx}
                            {window_width}
                            on:message={(evt) => (event = evt)}
                            bind:hideTooltip
                        />

                        <Html pointerEvents={false}>
                            <Tooltip
                                {switchToHtmlMap}
                                placement="object"
                                evt={event}
                                let:detail
                                {hideTooltip}
                            >
                                <!-- For the tooltip, do another data join because the hover event only has the data from the geography data -->
                                {@const tooltipData = {
                                    ...detail.props,
                                }}

                                {#if tooltipData.type == "County"}
                                    {#if tooltipData.county}
                                        <div class="tooltip-content">
                                            {#if !isNaN(tooltipData.total)}
                                                <div class="tooltip-header">
                                                    <span class="tooltip-title">
                                                        {titleCase(
                                                            tooltipData.county
                                                        )}, {tooltipData.state_abbreviation}
                                                    </span>
                                                </div>

                                                {#if ctx["type"] != "tribe"}
                                                    <!-- homepage or institution page -->
                                                    <div>
                                                        {ctx["type"] ==
                                                        "homepage"
                                                            ? guff.map_tooltip_county1
                                                            : name +
                                                              " " +
                                                              guff.map_tooltip_geo1}

                                                        {@html formatRemainsOf(
                                                            tooltipData.total,
                                                            false,
                                                            false,
                                                            true
                                                        )}
                                                        {guff.map_tooltip_geo2}
                                                        {titleCase(
                                                            tooltipData.county
                                                        )} County.
                                                        {#if tooltipData.total == 1}
                                                            {guff.map_tooltip_geo3}
                                                            {#if tooltipData.pct_complete == 0}
                                                                <span
                                                                    class="tooltip-pct-complete"
                                                                    style="background: {colorScale(
                                                                        tooltipData.pct_complete
                                                                    )};"
                                                                >
                                                                    {guff.map_tooltip_geo4_not}
                                                                </span>
                                                                {guff.map_tooltip_geo4}
                                                            {:else}
                                                                <span
                                                                    class="tooltip-pct-complete"
                                                                    style="background: {colorScale(
                                                                        tooltipData.pct_complete
                                                                    )};"
                                                                >
                                                                    {guff.map_tooltip_geo4}
                                                                </span>
                                                            {/if}
                                                            {guff.map_tooltip_geo5}
                                                        {:else}
                                                            <span
                                                                class="tooltip-pct-complete"
                                                                style="background: {colorScale(
                                                                    tooltipData.pct_complete
                                                                )};"
                                                            >
                                                                {#if tooltipData.pct_complete == 0}
                                                                    0%
                                                                {:else if tooltipData.pct_complete < 0.0095}
                                                                    {(
                                                                        tooltipData.pct_complete *
                                                                        100
                                                                    ).toFixed(
                                                                        1
                                                                    )}%
                                                                {:else if tooltipData.pct_complete > 0.995 && tooltipData.pct_complete < 1.0}
                                                                    Almost {(
                                                                        tooltipData.pct_complete *
                                                                        100
                                                                    ).toFixed(
                                                                        0
                                                                    )}%
                                                                {:else}
                                                                    {(
                                                                        tooltipData.pct_complete *
                                                                        100
                                                                    ).toFixed(
                                                                        0
                                                                    )}%
                                                                {/if}
                                                            </span>
                                                            were {guff.map_tooltip_geo4}
                                                            {guff.map_tooltip_geo5}
                                                        {/if}
                                                    </div>
                                                {:else}
                                                    <!-- tribe page  -->
                                                    {guff.map_tooltip_geo_tribe1a}
                                                    {@html formatRemainsOf(
                                                        tooltipData.total,
                                                        false,
                                                        false,
                                                        true
                                                    )}
                                                    {guff.map_tooltip_geo2}
                                                    {titleCase(
                                                        tooltipData.county
                                                    )} County
                                                    {guff.map_tooltip_geo_tribe1c}
                                                    {name}.
                                                {/if}
                                            {:else}
                                                <div class="tooltip-header">
                                                    <span class="tooltip-title">
                                                        {titleCase(
                                                            tooltipData.county
                                                        )}, {tooltipData.state_abbreviation}
                                                    </span>
                                                </div>
                                                {guff.map_tooltip_county_empty}
                                                {titleCase(tooltipData.county)} County.
                                            {/if}
                                        </div>
                                    {/if}
                                {:else if tooltipData.type == "Institution"}
                                    <div class="tooltip-content">
                                        <div class="tooltip-header">
                                            <svg
                                                class="institution-icon"
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 512 512"
                                                ><!--! Font Awesome Pro 6.2.0 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2022 Fonticons, Inc. --><path
                                                    d="M240.1 4.2c9.8-5.6 21.9-5.6 31.8 0l171.8 98.1L448 104l0 .9 47.9 27.4c12.6 7.2 18.8 22 15.1 36s-16.4 23.8-30.9 23.8H32c-14.5 0-27.2-9.8-30.9-23.8s2.5-28.8 15.1-36L64 104.9V104l4.4-1.6L240.1 4.2zM64 224h64V416h40V224h64V416h48V224h64V416h40V224h64V420.3c.6 .3 1.2 .7 1.8 1.1l48 32c11.7 7.8 17 22.4 12.9 35.9S494.1 512 480 512H32c-14.1 0-26.5-9.2-30.6-22.7s1.1-28.1 12.9-35.9l48-32c.6-.4 1.2-.7 1.8-1.1V224z"
                                                /></svg
                                            >
                                            <span class="tooltip-title"
                                                >{tooltipData.main_institution_short_name}</span
                                            >
                                            {#if tooltipData.sub_unit != "NA"}
                                                <div class="subunit">
                                                    {tooltipData.sub_unit}
                                                </div>
                                            {/if}
                                        </div>
                                        {#if ctx["type"] != "tribe"}
                                            <div>
                                                {guff.map_tooltip_institution1a}
                                                <span
                                                    class="tooltip-pct-complete"
                                                    style="background: {colorScale(
                                                        tooltipData.pct_complete
                                                    )};"
                                                >
                                                    {#if tooltipData.pct_complete == 0}
                                                        0%
                                                    {:else if tooltipData.pct_complete < 0.0095}
                                                        {(
                                                            tooltipData.pct_complete *
                                                            100
                                                        ).toFixed(1)}%
                                                    {:else if tooltipData.pct_complete > 0.995 && tooltipData.pct_complete < 1.0}
                                                        Almost {(
                                                            tooltipData.pct_complete *
                                                            100
                                                        ).toFixed(0)}%
                                                    {:else}
                                                        {(
                                                            tooltipData.pct_complete *
                                                            100
                                                        ).toFixed(0)}%
                                                    {/if}
                                                </span>
                                                of
                                                {@html formatRemainsOf(
                                                    tooltipData.total,
                                                    false,
                                                    false,
                                                    true
                                                )}
                                                {guff.map_tooltip_institution1c2}
                                            </div>
                                        {:else}
                                            <div>
                                                {guff.map_tooltip_institution1a}
                                                {@html formatRemainsOf(
                                                    tooltipData.total,
                                                    false,
                                                    false,
                                                    true
                                                )}
                                                {guff.map_tooltip_institution1c1}
                                            </div>
                                        {/if}
                                    </div>
                                {:else if tooltipData.type == "State"}
                                    <div class="tooltip-content">
                                        <div class="tooltip-header">
                                            <span class="tooltip-title"
                                                >{titleCase(
                                                    tooltipData.state
                                                )}</span
                                            >
                                        </div>

                                        {#if ctx["type"] == "institution"}
                                            <!-- institution page -->
                                            <div>
                                                {the(name, true) +
                                                    " " +
                                                    guff.map_tooltip_geo1}

                                                {@html formatRemainsOf(
                                                    tooltipData.total,
                                                    false,
                                                    false,
                                                    true
                                                )}
                                                {guff.map_tooltip_geo2}
                                                {titleCase(tooltipData.state)}.
                                                {#if tooltipData.total == 1}
                                                    {guff.map_tooltip_geo3}
                                                    {#if tooltipData.pct_complete == 0}
                                                        <span
                                                            class="tooltip-pct-complete"
                                                            style="background: {colorScale(
                                                                tooltipData.pct_complete
                                                            )};"
                                                        >
                                                            {guff.map_tooltip_geo4_not}
                                                        </span>
                                                        {guff.map_tooltip_geo4}
                                                    {:else}
                                                        <span
                                                            class="tooltip-pct-complete"
                                                            style="background: {colorScale(
                                                                tooltipData.pct_complete
                                                            )};"
                                                        >
                                                            {guff.map_tooltip_geo4}
                                                        </span>
                                                    {/if}
                                                    {guff.map_tooltip_geo5}

                                                    {#if tooltipData.unknown_county_total != tooltipData.total && tooltipData.unknown_county_total > 0}
                                                        {guff.map_tooltip_state7}
                                                    {/if}
                                                {:else}
                                                    <span
                                                        class="tooltip-pct-complete"
                                                        style="background: {colorScale(
                                                            tooltipData.pct_complete
                                                        )};"
                                                    >
                                                        {#if tooltipData.pct_complete == 0}
                                                            0%
                                                        {:else if tooltipData.pct_complete < 0.0095}
                                                            {(
                                                                tooltipData.pct_complete *
                                                                100
                                                            ).toFixed(1)}%
                                                        {:else if tooltipData.pct_complete > 0.995 && tooltipData.pct_complete < 1.0}
                                                            Almost {(
                                                                tooltipData.pct_complete *
                                                                100
                                                            ).toFixed(0)}%
                                                        {:else}
                                                            {(
                                                                tooltipData.pct_complete *
                                                                100
                                                            ).toFixed(0)}%
                                                        {/if}
                                                    </span>
                                                    were {guff.map_tooltip_geo4}
                                                    {guff.map_tooltip_geo5}

                                                    {#if tooltipData.unknown_county_total > 0}
                                                        {#if tooltipData.unknown_county_total != tooltipData.total}
                                                            {guff.map_tooltip_state9}
                                                            {formatRemainsOf(
                                                                tooltipData.unknown_county_total
                                                            )}
                                                            {guff.map_tooltip_state8}
                                                        {:else}
                                                            {guff.map_tooltip_state7}
                                                        {/if}
                                                    {/if}
                                                {/if}
                                            </div>
                                        {:else}
                                            <div>
                                                <!-- tribe page -->
                                                {guff.map_tooltip_geo_tribe1a}
                                                {@html formatRemainsOf(
                                                    tooltipData.total,
                                                    false,
                                                    false,
                                                    true
                                                )}
                                                {guff.map_tooltip_geo2}
                                                {titleCase(tooltipData.state)}
                                                {guff.map_tooltip_geo_tribe1c}
                                                {name}.

                                                {#if tooltipData.unknown_county_total != tooltipData.total && tooltipData.unknown_county_total > 0}
                                                    The {@html formatRemainsOf(
                                                        tooltipData.unknown_county_total
                                                    )}
                                                    {guff.map_tooltip_state8}
                                                {/if}
                                            </div>
                                        {/if}
                                    </div>
                                {/if}
                            </Tooltip>
                        </Html>
                    </LayerCake>
                {/if}
            </div>
        </div>
    </div>
</div>

<style>
    /*
      The wrapper div needs to have an explicit width and height in CSS.
      It can also be a flexbox child or CSS grid element.
      The point being it needs dimensions since the <LayerCake> element will
      expand to fill it.
    */
    .chart-container {
        width: calc(90%);
        aspect-ratio: 16/9;
    }

    .tooltip-content {
        padding: var(--spacing-1);
    }

    .institution-icon {
        width: 0.7rem;
        height: 0.7rem;
        display: inline;
        position: relative;
        top: 1px;
        margin-right: 2px;
    }

    .tooltip-pct-complete {
        padding: 2px 4px;
        color: white;
        font-weight: 600;
        display: inline;
        line-height: var(--line-height-2);
    }

    .tooltip-title {
        font-weight: 600;
        font-size: var(--scale-1);
        line-height: 1rem;
    }

    .subunit {
        font-size: var(--scale-3);
        margin-bottom: var(--spacing-2);
        font-style: italic;
        color: var(--font-gray);
        font-weight: 400;
    }
</style>
